import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Skeleton,
  Snackbar,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Link as LinkIcon,
  PushPin as PinterestIcon,
} from '@mui/icons-material';
import type { DataSourceHealth } from '../../types/dataHealth.types';
import type { IntegrationStatus } from '../../services/siteIntegrationService';
import { initiateOAuthFlow } from '../../utils/oauthPopupHelper';

interface PinterestSetupPanelProps {
  health?: DataSourceHealth;
  isLoading: boolean;
  siteId?: number;
  siteName?: string;
  /** Pre-fetched integration status from unified endpoint (optional) */
  integrationStatus?: IntegrationStatus;
}

export const PinterestSetupPanel: React.FC<PinterestSetupPanelProps> = ({ 
  health, 
  isLoading, 
  siteId,
  siteName, 
  integrationStatus 
}) => {
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleOAuthCallback = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'oauth-callback' && event.data?.provider === 'pinterest') {
        if (event.data.status === 'success') {
          setSnackbar({ open: true, message: 'Pinterest connected successfully!', severity: 'success' });
          setTimeout(() => window.location.reload(), 1500);
        } else {
          setSnackbar({ open: true, message: event.data.message || 'OAuth connection failed', severity: 'error' });
        }
      }
    };
    window.addEventListener('message', handleOAuthCallback);
    return () => window.removeEventListener('message', handleOAuthCallback);
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ px: 3 }}>
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }

  // Use integration status if available, fallback to health status
  const isConnected = integrationStatus?.connected ?? false;
  const isExpired = integrationStatus?.expired ?? false;
  const isConfigured = isConnected && !isExpired;

  return (
    <Box sx={{ px: 3 }}>
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="h5" gutterBottom>
                <PinterestIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#E60023' }} />
                Pinterest Business Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect your Pinterest Business account to track pins, boards, and audience engagement{siteName ? ` for ${siteName}` : ''}.
              </Typography>
            </Box>
            <Chip
              label={isConfigured ? 'Connected' : isExpired ? 'Token Expired' : 'Not Connected'}
              color={isConfigured ? 'success' : isExpired ? 'warning' : 'default'}
              icon={isConfigured ? <CheckIcon /> : isExpired ? <ScheduleIcon /> : <ErrorIcon />}
            />
          </Box>

          <Box my={3}>
            <Typography variant="subtitle2" gutterBottom>
              What you'll get:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Pinterest analytics and engagement metrics" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Pin performance tracking and audience insights" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Board organization and content strategy data" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Visual content SEO optimization opportunities" />
              </ListItem>
            </List>
          </Box>

          <Alert severity={isConfigured ? 'success' : isExpired ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {isConfigured ? (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  ✓ Pinterest Business Connected for this site
                </Typography>
                {integrationStatus?.expiresAt && (
                  <Typography variant="caption">
                    Token expires: {new Date(integrationStatus.expiresAt).toLocaleString()}
                  </Typography>
                )}
                {health && (
                  <Typography variant="caption" display="block">
                    Last Data Sync: {health.lastSuccessfulSync ? new Date(health.lastSuccessfulSync).toLocaleString() : 'Never'}
                  </Typography>
                )}
              </Box>
            ) : isExpired ? (
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  ⚠ Pinterest Token Expired - Reconnection Required
                </Typography>
                <Typography variant="caption" display="block">
                  {integrationStatus?.message || 'Your OAuth token has expired. Please reconnect to continue syncing data.'}
                </Typography>
                {integrationStatus?.expiresAt && (
                  <Typography variant="caption" display="block">
                    Token expired: {new Date(integrationStatus.expiresAt).toLocaleString()}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2">
                Pinterest Business is not connected for this site. Connect your account to start importing Pinterest data.
              </Typography>
            )}
          </Alert>

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => siteId && initiateOAuthFlow('pinterest', siteId)}
              disabled={!siteId}
              sx={{ 
                bgcolor: '#E60023', 
                '&:hover': { bgcolor: '#ad081b' },
              }}
            >
              {isConfigured ? 'Reconnect Pinterest' : 'Connect Pinterest'}
            </Button>
          </Box>

          {!siteId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select a site above to connect Pinterest.
            </Alert>
          )}
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};
